import React, { useMemo } from 'react'
import { Box, Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import BigNumber from 'bignumber.js'
import PawLabel from 'components/Text/PawLabel'
import TextShadow from 'components/Text/TextShadow'
import Value from 'components/Value/Value'

import { useBurnedBalance, useTotalSupply, useTotalPuppySupply, useTotalGodfatherSupply } from 'hooks/useTokenBalance'
import { usePriceTVL, useVaultTVL, useVaultRaffle, usePriceBnbBusd } from 'state/hooks'
import { differenceInDays } from 'date-fns'
import { getBalanceNumber } from 'utils/formatBalance'
import { getSmoyAddress } from 'utils/addressHelpers'

import {
  LOCKED_DEV_SMOY,
  LOCKED_Q4REWARD_SMOY,
  LOCKED_MARKETING_SMOY,
  SMOY_PER_BLOCK,
  TOTAL_SMOY_BURNED,
} from 'config/index'

const useStyles = makeStyles((theme) => ({
  overviewStatusCard: {
    fontFamily: "'Dosis', sans-serif",
    backgroundColor: '#FEC2DC',
    position: 'relative',
    zIndex: 11,
    padding: '16px',
    borderRadius: '8px',
    '& .mainCover': {
      marginLeft: '0px',
    },
    '& .bottomLeft': {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '48px',
    },
    '& .topHeader': {
      backgroundColor: '#fff',
      padding: '16px 16px',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      '& .tvl': {
        margin: '8px 0 0 52px',
      },
    },
    '& .detailCover': {
      backgroundColor: '#FFDAEC',
      marginTop: '8px',
      padding: '16px',
      borderRadius: '8px',
    },
    '& .whiteBox': {
      backgroundColor: '#fff',
      borderRadius: '8px',
      display: 'flex',
      padding: '0 8px',
      margin: '8px 8px 8px 8px',
      fontWeight: 800,
      fontSize: '16px',
      '& .left': {
        color: '#939393',
      },
      '& .right': {
        margin: '0 0 0 auto',
        color: '#52494C',
      },
    },
  },
  [theme.breakpoints.up('sm')]: {
    overviewStatusCard: {
      padding: '16px',
      borderRadius: '8px',
      '& .mainCover': {
        marginLeft: '0px',
      },
      '& .bottomLeft': {
        bottom: 0,
        left: 0,
        width: '72px',
        display: 'block',
      },
      '& .topHeader': {
        padding: '16px 24px',
        flexDirection: 'row',
        '& .tvl': {
          margin: '0 0 0 auto',
        },
      },
      '& .detailCover': {
        marginTop: '8px',
        padding: '16px',
        borderRadius: '8px',
      },
      '& .whiteBox': {
        backgroundColor: '#fff',
        borderRadius: '8px',
        display: 'flex',
        padding: '0 8px',
        margin: '8px 0 0 36px',
        fontWeight: 800,
        fontSize: '14px',
      },
    },
  },
  [theme.breakpoints.up('lg')]: {
    overviewStatusCard: {
      padding: '16px',
      '& .mainCover': {
        marginLeft: '250px',
      },
      '& .bottomLeft': {
        position: 'absolute',
        bottom: 0,
        left: 10,
        width: '280px',
      },
      '& .topHeader': {
        padding: '16px 72px',
        display: 'flex',
      },
      '& .detailCover': {
        marginTop: '8px',
        padding: '16px',
      },
      '& .whiteBox': {
        display: 'flex',
        padding: '0 8px',
        margin: '8px 8px 0 46px',
        fontSize: '16px',
        '& .left': {
          color: '#939393',
        },
        '& .right': {
          margin: '0 0 0 auto',
          color: '#52494C',
        },
      },
    },
  },
}))

const OverviewStatusCard = () => {
  const classes = useStyles()
  const bnbPrice = usePriceBnbBusd()
  const totalTVL = usePriceTVL().toNumber()
  const totalSupply = useTotalSupply()
  const totalPuppySupply = useTotalPuppySupply()
  const totalGodfatherSupply = useTotalGodfatherSupply()
  const burnedBalance = getBalanceNumber(useBurnedBalance(getSmoyAddress()))
  const vaultTVL = useVaultTVL().toNumber()
  const raffle = useVaultRaffle()

  const puppySupply = useMemo(() => {
    const _totalSupply = getBalanceNumber(totalPuppySupply, 0)
    return _totalSupply
  }, [totalPuppySupply])

  const godfatherSupply = useMemo(() => {
    const _totalSupply = getBalanceNumber(totalGodfatherSupply, 0)
    return _totalSupply
  }, [totalGodfatherSupply])

  const smoySupply = useMemo(() => {
    let _totalSupply = getBalanceNumber(totalSupply)

    // Latte's deal, 800,000 SMOY in 120 days
    const latteTotalSupply = 800000
    const latteDays = differenceInDays(new Date(), new Date('2021-11-11')) + 1
    let latteSupply = 0
    if (latteDays > 0) {
      latteSupply = latteDays >= 120 ? latteTotalSupply : Math.round((latteTotalSupply / 120) * latteDays)
    }

    const lockedMarketingSmoy = LOCKED_MARKETING_SMOY - latteSupply

    _totalSupply = _totalSupply - LOCKED_DEV_SMOY - LOCKED_Q4REWARD_SMOY - burnedBalance - lockedMarketingSmoy

    return _totalSupply
  }, [totalSupply, burnedBalance])

  const samoyedTVL = useMemo(() => {
    const raffleTVL =
      raffle && raffle.raffleBalance ? new BigNumber(raffle.raffleBalance).div(1e18).times(bnbPrice).toNumber() : 0

    return totalTVL + vaultTVL + raffleTVL
  }, [totalTVL, vaultTVL, raffle, bnbPrice])

  return (
    <Box className={classes.overviewStatusCard}>
      <img className="bottomLeft" src="/images/Samoyed-with-glass.svg" alt="Samoyed" />
      <Box className="mainCover">
        <Box className="topHeader">
          <PawLabel color="pink" size="36" textColor="black" textSize="36" text="Current TVL" />
          <span className="tvl">
            <TextShadow color="yellow" size="36" colorBorder="black" sizeBorder="3" lineHeight="48">
              {(samoyedTVL || 0) <= 0 ? 'N/A' : samoyedTVL.toLocaleString('en-US', { maximumFractionDigits: 0 })} $USD
            </TextShadow>
          </span>
        </Box>
        <Box className="detailCover">
          <Grid container>
            <Grid item sm={6} xs={12}>
              <PawLabel color="soft-pink" size="36" textColor="black" textSize="24" text="NFT Stats (Current Season)" />
              <Box className="whiteBox">
                <span className="left">Total Puppy Released</span>
                <span className="right">
                  <Value value={puppySupply} decimals={0} />
                </span>
              </Box>
              <Box className="whiteBox">
                <span className="left">Total Godfather Released</span>
                <span className="right">
                  <Value value={godfatherSupply} decimals={0} />
                </span>
              </Box>
            </Grid>
            <Grid item sm={6} xs={12}>
              <PawLabel color="soft-pink" size="36" textColor="black" textSize="24" text="$SMOY Stats" />
              <Box className="whiteBox">
                <span className="left">Circulating</span>
                <span className="right">
                  <Value value={smoySupply} decimals={0} />
                </span>
              </Box>
              <Box className="whiteBox">
                <span className="left">Total Burned</span>
                <span className="right">
                  <Value value={TOTAL_SMOY_BURNED} decimals={0} />
                </span>
              </Box>
              <Box className="whiteBox">
                <span className="left">New Block</span>
                <span className="right">{SMOY_PER_BLOCK.toString()}</span>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

export default OverviewStatusCard
